import { z } from 'zod';
import { cursorQuerySchema } from '../common/pagination';

/**
 * Leden uitnodigen die nog nooit toegang hadden (ADR-0101).
 *
 * Na de import van het oude platform staan de leden als account met status `disabled`, zonder
 * wachtwoord. De beheerder nodigt ze per pakje uit: elk lid krijgt een link om zelf een wachtwoord
 * te kiezen, en pas dan staat zijn account aan. Wie bewust geblokkeerd werd of vroeg om verwijderd
 * te worden, staat nooit in deze lijst.
 */

/** `not_invited`: nog nooit een mail; `invited`: een link staat open (of de mail faalde). */
export const memberInvitationStateSchema = z.enum(['not_invited', 'invited']);
export type MemberInvitationState = z.infer<typeof memberInvitationStateSchema>;

/** `pending`: de rij bestaat, de mail vertrok nog niet (een pakje dat onderbroken werd). */
export const memberInvitationMailStatusSchema = z.enum(['sent', 'failed', 'pending']);
export type MemberInvitationMailStatus = z.infer<typeof memberInvitationMailStatusSchema>;

export const memberInvitationQuerySchema = cursorQuerySchema.extend({
  state: z.enum(['not_invited', 'invited', 'all']).default('not_invited'),
  /** Zoeken op naam of e-mailadres. */
  q: z.string().trim().max(100).optional(),
});
export type MemberInvitationQuery = z.infer<typeof memberInvitationQuerySchema>;

export const memberInvitationCandidateSchema = z.object({
  accountId: z.guid(),
  personId: z.guid(),
  displayName: z.string(),
  email: z.string(),
  state: memberInvitationStateSchema,
  /** Wanneer de laatste mail vertrok; leeg als er nog nooit een vertrok. */
  lastSentAt: z.string().datetime().nullable(),
  /** Tot wanneer de link werkt. */
  expiresAt: z.string().datetime().nullable(),
  mailStatus: memberInvitationMailStatusSchema.nullable(),
  /** Vanaf wanneer opnieuw versturen mag (24 uur na een verzonden mail); leeg = nu al. */
  resendAllowedAt: z.string().datetime().nullable(),
});
export type MemberInvitationCandidate = z.infer<typeof memberInvitationCandidateSchema>;

/** De tellers bovenaan de kaart, over alle pagina's heen. */
export const memberInvitationCountsSchema = z.object({
  notInvited: z.number().int().min(0),
  invited: z.number().int().min(0),
  /** Uitgenodigd en intussen een wachtwoord gekozen. */
  accepted: z.number().int().min(0),
});
export type MemberInvitationCounts = z.infer<typeof memberInvitationCountsSchema>;

export const memberInvitationPageSchema = z.object({
  items: z.array(memberInvitationCandidateSchema),
  nextCursor: z.string().nullable(),
  counts: memberInvitationCountsSchema,
});
export type MemberInvitationPage = z.infer<typeof memberInvitationPageSchema>;

/** Hoogstens zoveel leden per verzoek; de web-app stuurt grotere selecties in pakjes. */
export const MEMBER_INVITE_BATCH_MAX = 100;

/**
 * Een selectie (`accountIds`) of `all`: de volgende leden die nog nooit uitgenodigd werden, tot
 * `MEMBER_INVITE_BATCH_MAX`. Precies één van de twee.
 */
export const inviteMembersRequestSchema = z
  .object({
    accountIds: z.array(z.guid()).min(1).max(MEMBER_INVITE_BATCH_MAX).optional(),
    all: z.literal(true).optional(),
  })
  .refine((value) => (value.accountIds ? !value.all : value.all === true), {
    message: 'Geef accountIds of all, niet allebei en niet geen van beide.',
  });
export type InviteMembersRequest = z.infer<typeof inviteMembersRequestSchema>;

export const memberInviteSkipReasonSchema = z.enum([
  /** Niet (meer) uit te nodigen: actief, geblokkeerd, verwijderd, of geen e-mailadres. */
  'not_eligible',
  /** De vorige mail vertrok minder dan 24 uur geleden. */
  'recently_invited',
  /** Er staat al een andere uitnodiging open voor dit adres. */
  'other_invitation',
]);
export type MemberInviteSkipReason = z.infer<typeof memberInviteSkipReasonSchema>;

export const inviteMembersResultSchema = z.object({
  /** Uitnodigingen aangemaakt of vernieuwd in dit verzoek. */
  invited: z.number().int().min(0),
  /** Daarvan vertrok de mail niet (bv. omdat mail voor deze organisatie nog niet mag). */
  mailFailed: z.number().int().min(0),
  skipped: z.array(z.object({ accountId: z.guid(), reason: memberInviteSkipReasonSchema })),
  /** Hoeveel leden er daarna nog nooit uitgenodigd werden. */
  remaining: z.number().int().min(0),
});
export type InviteMembersResult = z.infer<typeof inviteMembersResultSchema>;
